import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormHelperText } from "@material-ui/core";
import { useInput } from "react-admin";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { useForm } from "react-final-form";
import { apiUrl } from "../../dataProvider";
import { sortAlphabetically } from "../../utils/sortAlphabetically";

/**
 * SearchableSelect (also exported as CustomSelect)
 *
 * Searchable dropdown used in forms.
 * Single select by default. Set isMulti for many values.
 * Loads options from an API (resource) or from a local list (staticOptions).
 *
 * Props:
 * source - form field name this dropdown writes to
 * label - text shown above the dropdown
 * isMulti - false = pick one, true = pick many
 * resource - API path used to load options (async)
 * staticOptions - local array of options; skips the API when set
 * optionValue - which field is saved (id, sku_code, ...)
 * optionLabel - which field is shown in the list (name, store_name, ...)
 * validate - form rules, e.g. required
 * query - extra API query filters
 * pageSize - how many API rows to load
 * paramId - extra id in the API url when needed
 * idsource - other form field to store the option name
 * sideSource - other form field to store a side value
 * sideValueKey - which option field goes into sideSource
 * clearSourcesOnChange - form fields to empty when the value changes
 * isDisabled - disable the dropdown
 * defaultValue - starting value
 * baseUrl - override API host
 * transformOptions - reshape API data before showing it
 * skipPaging - do not send page number/size
 * requireQueryKeys - wait to fetch until these query keys have values
 * placeholder - empty-state text
 * menuPlacement - where the list opens (auto, top, bottom)
 * closeMenuOnSelect - close the list after a pick (usually false for multi)
 * onValueChange - extra callback after a pick
 * sortAlphabetically - sort A-Z (default true). false keeps API/list order
 * nullOption - optional first choice for empty value (e.g. Default). Selecting it saves null
 * isEdit - unused; kept so old callers do not break
 */
export const CustomSelect = ({
  label,
  isEdit,
  isMulti = false,
  optionValue = "sku_code",
  optionLabel = "name",
  validate = [],
  query = {},
  pageSize = 1000,
  paramId,
  idsource = "",
  sideSource = "",
  sideValueKey = "sku_code",
  clearSourcesOnChange = [],
  isDisabled = false,
  source,
  resource,
  defaultValue = null,
  baseUrl,
  transformOptions,
  skipPaging = false,
  requireQueryKeys = [],
  placeholder,
  menuPlacement = "auto",
  closeMenuOnSelect = true,
  staticOptions,
  onValueChange,
  sortAlphabetically: sortAz = true,
  nullOption,
}) => {
  const [list, setList] = useState({
    data: [],
    raw: [],
  });
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const listRawRef = useRef([]);
  const form = useForm();

  const {
    input,
    isRequired,
    meta: { touched, error },
  } = useInput({ source, validate, value: defaultValue });

  const { input: inputSourceId } = useInput({ source: idsource || "test" });

  const getOptionText = (item) => {
    if (item?.isDefault) {
      return String(
        item?.[optionLabel] ?? item?.name ?? nullOption?.[optionLabel] ?? "Default"
      );
    }
    return String(
      item?.[optionLabel] ?? item?.name ?? item?.[optionValue] ?? item?.id ?? ""
    );
  };

  const getOptionId = (item) =>
    item?.isDefault ? null : item?.[optionValue] ?? item?.id;

  const withNullOption = (options = []) => {
    if (!nullOption) return options;
    const withoutDefault = options.filter((item) => !item?.isDefault);
    return [nullOption, ...withoutDefault];
  };

  const getLists = (params) => {
    const apiBase = baseUrl || apiUrl;
    const url = paramId ? `${apiBase}/${resource}/${paramId}` : `${apiBase}/${resource}`;
    return axios.get(url, { params }).then(({ data }) => {
      const payload = data?.data ?? data;
      const nextList =
        typeof transformOptions === "function"
          ? transformOptions(payload)
          : Array.isArray(payload)
          ? payload
          : [];
      const sorted = sortAz
        ? sortAlphabetically(nextList, (item) => getOptionText(item))
        : nextList;
      return withNullOption(sorted);
    });
  };

  const filterOptions = (value, options = listRawRef.current) => {
    const search = String(value || "").toLowerCase().trim();
    if (!search) return options;

    return options.filter((item) => {
      const text = getOptionText(item).toLowerCase();
      const id = String(getOptionId(item) ?? "").toLowerCase();
      return text.includes(search) || id.includes(search);
    });
  };

  const getSearchOptions = (value, callback) => {
    callback(filterOptions(value, listRawRef.current));
  };

  const setStoreList = useCallback(
    async (params) => {
      try {
        setLoading(true);
        const response = await getLists(params);
        listRawRef.current = response;
        setList({ data: response, raw: response });
      } catch ({ response }) {
        listRawRef.current = [];
        setList({ data: [], raw: [] });

        const status = response?.status;
        if (status === 401 || status === 403) {
          window.location.replace("/#/login");
        }
      } finally {
        setLoading(false);
      }
    },
    [paramId, baseUrl, resource, transformOptions] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const hasRequiredQuery =
    !requireQueryKeys.length ||
    requireQueryKeys.every((key) => query?.[key] != null && query[key] !== "");

  const queryKey = JSON.stringify(query);

  useEffect(() => {
    if (Array.isArray(staticOptions)) {
      const next = sortAz
        ? sortAlphabetically(staticOptions, (item) => getOptionText(item))
        : staticOptions;
      const withDefault = withNullOption(next);
      listRawRef.current = withDefault;
      setList({ data: withDefault, raw: withDefault });
      return;
    }

    if (!hasRequiredQuery) {
      const empty = withNullOption([]);
      listRawRef.current = empty;
      setList({ data: empty, raw: empty });
      return;
    }

    setStoreList(
      skipPaging
        ? { ...query }
        : {
            "page[number]": 1,
            "page[size]": pageSize,
            ...query,
          }
    );
  }, [hasRequiredQuery, queryKey, setStoreList, paramId, staticOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (callback) => (selectedopt) => {
    const isCleared = selectedopt == null || selectedopt?.isDefault;

    if (idsource) {
      inputSourceId.onChange(isCleared ? null : selectedopt?.name ?? null);
    }
    if (sideSource) {
      form.change(
        sideSource,
        isCleared
          ? null
          : isMulti
          ? Array.isArray(selectedopt)
            ? selectedopt.map((item) => item?.[sideValueKey]).filter(Boolean)
            : []
          : selectedopt?.[sideValueKey] ?? null
      );
    }
    if (Array.isArray(clearSourcesOnChange) && clearSourcesOnChange.length > 0) {
      clearSourcesOnChange.forEach((clearSource) => {
        form.change(clearSource, []);
      });
    }
    if (isMulti) {
      const nextValue = Array.isArray(selectedopt)
        ? selectedopt.map((item) => getOptionId(item)).filter((id) => id != null)
        : [];
      callback(nextValue);
      if (typeof onValueChange === "function") onValueChange(nextValue);
    } else if (isCleared) {
      callback(null);
      if (typeof onValueChange === "function") onValueChange(null);
    } else {
      const nextValue = getOptionId(selectedopt);
      callback(nextValue);
      if (typeof onValueChange === "function") onValueChange(nextValue ?? "");
    }
    setSearchValue("");
  };

  const findSelectedOption = (value) => {
    if (value == null || value === "") {
      return nullOption || undefined;
    }
    if (typeof value === "object" && value.feature_group != null) {
      return list.raw.find(
        (item) => String(item?.[optionValue] ?? "") === String(value.feature_group)
      );
    }
    const needle = String(value);
    return (
      list.raw.find((item) => !item?.isDefault && String(getOptionId(item) ?? "") === needle) ||
      list.raw.find((item) => String(item?.name ?? "") === needle) ||
      list.raw.find((item) => String(item?.[optionLabel] ?? "") === needle)
    );
  };

  const selectedValue = isMulti
    ? list.raw.filter((item) => {
        if (item?.isDefault) return false;
        const selected = Array.isArray(input.value) ? input.value : [];
        return selected
          .map((entry) => String(entry))
          .includes(String(getOptionId(item) ?? ""));
      })
    : findSelectedOption(input.value);

  useEffect(() => {
    if (!sideSource || isMulti) return;
    const nextSideValue = selectedValue?.[sideValueKey] ?? null;
    const currentSideValue = form.getState().values?.[sideSource];
    if (currentSideValue !== nextSideValue) {
      form.change(sideSource, nextSideValue);
    }
  }, [selectedValue, sideSource, sideValueKey, isMulti, form]);

  return (
    <div style={{ marginBottom: "4px", width: "100%" }}>
      {label ? (
        <p
          style={{
            margin: "8px 8px 8px 2px",
            fontSize: 12,
            color: "#757575",
          }}
        >
          {label} {isRequired ? "*" : ""}
        </p>
      ) : null}
      <AsyncSelect
        name={input.name}
        onBlur={input.onBlur}
        isClearable
        isSearchable
        styles={{
          container: (provided) => ({ ...provided, width: "100%" }),
          control: (provided) => ({
            ...provided,
            minHeight: 38,
          }),
          valueContainer: (provided) =>
            isMulti
              ? {
                  ...provided,
                  maxHeight: 72,
                  overflowY: "auto",
                  flexWrap: "wrap",
                }
              : provided,
          menu: (provided) => ({ ...provided, zIndex: 9999 }),
          menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? "#2684FF"
              : state.isFocused
              ? "#DEEBFF"
              : provided.backgroundColor,
            color: state.isSelected ? "#fff" : provided.color,
          }),
        }}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        blurInputOnSelect={closeMenuOnSelect}
        hideSelectedOptions={false}
        isOptionSelected={(option, selectValue) => {
          const selected = Array.isArray(selectValue)
            ? selectValue
            : selectValue
            ? [selectValue]
            : [];
          const optionId = String(getOptionId(option) ?? "");
          return selected.some(
            (item) => String(getOptionId(item) ?? "") === optionId
          );
        }}
        isLoading={loading}
        inputValue={searchValue}
        defaultOptions={list.raw}
        isDisabled={isDisabled}
        placeholder={placeholder}
        getOptionLabel={getOptionText}
        getOptionValue={(e) =>
          e?.isDefault ? "__default__" : String(getOptionId(e) ?? "")
        }
        loadOptions={getSearchOptions}
        value={selectedValue || (isMulti ? [] : null)}
        onChange={handleChange(input.onChange)}
        onInputChange={(value, meta) => {
          if (meta.action === "input-change") {
            setSearchValue(value);
            return value;
          }
          if (meta.action === "menu-close" || meta.action === "set-value") {
            setSearchValue("");
            return "";
          }
          return searchValue;
        }}
        filterOption={null}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        menuPlacement={menuPlacement}
        maxMenuHeight={280}
      />
      {touched && error ? (
        <FormHelperText error>
          {error === "ra.validation.required" ? "Required" : error}
        </FormHelperText>
      ) : null}
    </div>
  );
};

export const SearchableSelect = CustomSelect;
export default CustomSelect;
